import React, { type CSSProperties, type ReactElement } from "react"
import styled, { useTheme } from "styled-components"
import { theme } from "@/common/Mixin"
import NextImg from "@/common/components/NextImg/index"
import GradientTop from "@/common/assets/image/products/nft/airdrop/Top.svg"
import EnvelopTop from "@/common/assets/image/Mainpage/hi/Top.svg"
import CheckIcon from "@/common/assets/image/contactUs/check.svg"
import {
  CheckList,
  CheckListItemDesc,
  CheckListItemTitle,
  CheckListItemWrapper,
  CheckListLayoutWrapper,
  CheckListTitleWrapper,
  CheckListWrapper
} from "./CheckListStyle"
import { HorizontalScroll } from "./HorizontalScroll"
import { useMediaQuery } from "@mui/material"
const Fade = require("react-reveal/Fade")

export interface SubDescription {
  icon?: any
  title?: string
  content?: string
  imageStyle?: CSSProperties
  imageWrapperStyle?: CSSProperties
}

export interface TitleProps {
  text: string
  textType?: "h2" | "h3" | "h4" | "span"
  textClass?: string
}

export interface DescriptionItem {
  wide?: boolean
  element?: any
  titles?: TitleProps[]
  content?: string | ReactElement
  contentBorder?: boolean
  checkList?: CheckList[]
  subDescriptions?: SubDescription[]
}
export interface DescriptionProps {
  items: DescriptionItem[]
  id?: string
  useHorizontalScroll?: boolean
  horizontalScrollTitle?: string
  backgroundType?: "gradient" | "envelope" | "none"
}

export default function Description({
  items,
  id,
  useHorizontalScroll = true,
  horizontalScrollTitle,
  backgroundType = "gradient"
}: DescriptionProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(`(${theme.lg})`)
  const renderCheckList = (checkList: CheckList[], wide: boolean) => (
    <CheckListLayoutWrapper
      style={{
        width: "100%",
        justifyContent: "space-evenly"
      }}
      flexDirection={wide ? "row" : "column"}
    >
      {checkList.map((list, index) => (
        <CheckListWrapper key={`${list.title}_${index}`}>
          <CheckListTitleWrapper className={list.titleClass ?? "body-bold"}>
            {list.title}
          </CheckListTitleWrapper>
          {list.list.map((item, index) => (
            <CheckListItemWrapper
              style={{
                ...(isMobile
                  ? {
                      gap: "8px"
                    }
                  : {})
              }}
              key={index}
            >
              <NextImg
                style={{ width: "24px" }}
                src={CheckIcon.src}
                alt="Check Icon"
              />
              {item.title && (
                <CheckListItemTitle className="body">
                  {item.title}
                </CheckListItemTitle>
              )}
              {item.description && (
                <CheckListItemDesc color={list.listColor} className="body">
                  {item.description}
                </CheckListItemDesc>
              )}
            </CheckListItemWrapper>
          ))}
        </CheckListWrapper>
      ))}
    </CheckListLayoutWrapper>
  )

  const renderSubDescriptions = (subDescriptions: SubDescription[]) => (
    <SubDescLayoutWrapper>
      {subDescriptions.map(
        ({ icon, title, content, imageStyle = {}, imageWrapperStyle = {} }) => (
          <SubDescWrapper key={title}>
            {icon && (
              <div style={{ paddingBottom: "12px", ...imageWrapperStyle }}>
                <NextImg
                  style={{ width: isMobile ? "100px" : "200px", ...imageStyle }}
                  src={icon.src}
                  alt={`${title} icon`}
                />
              </div>
            )}
            {title && (
              <SubDescTitleWrapper className="body-bold">
                {title}
              </SubDescTitleWrapper>
            )}
            {content && (
              <SubDescContent className="body">{content}</SubDescContent>
            )}
          </SubDescWrapper>
        )
      )}
    </SubDescLayoutWrapper>
  )

  const elements = items.map(
    (
      {
        element,
        titles,
        wide = false,
        content,
        contentBorder = true,
        checkList,
        subDescriptions
      },
      index
    ) => (
      <ItemWrapper wide={wide} key={index}>
        {element ?? (
          <CardWrapper wide={wide} key={index}>
            {titles &&
              titles.map(({ text, textType = "span", textClass }) => (
                <TitleWrapper
                  key={text}
                  className={textClass ?? (textType == "span" ? "title" : "")}
                  as={textType}
                  wide={wide}
                >
                  <Fade bottom key={text}>
                    {text}
                  </Fade>
                </TitleWrapper>
              ))}
            {content && (
              <DescWrapper
                contentBorder={contentBorder}
                className="body"
                wide={wide}
              >
                {content}
              </DescWrapper>
            )}
            {checkList && renderCheckList(checkList, wide)}
            {subDescriptions && renderSubDescriptions(subDescriptions)}
          </CardWrapper>
        )}
      </ItemWrapper>
    )
  )
  return (
    <div id={id}>
      {useHorizontalScroll && items.length > 2 && !isMobile ? (
        <HorizontalScroll
          title={horizontalScrollTitle}
          backgroundType={backgroundType}
          items={elements}
        />
      ) : (
        <Wrapper backgroundType={backgroundType}>
          {backgroundType !== "none" && (
            <TopWrapper>
              <NextImg
                src={backgroundType === "gradient" ? GradientTop : EnvelopTop}
              />
            </TopWrapper>
          )}
          <ContentWrapper className="kg-wrapper">{elements}</ContentWrapper>
          {backgroundType !== "none" && (
            <BottomWrapper>
              <NextImg
                src={backgroundType === "gradient" ? GradientTop : EnvelopTop}
              />
            </BottomWrapper>
          )}
        </Wrapper>
      )}
    </div>
  )
}

interface WrapperProps {
  backgroundType: "gradient" | "envelope" | "none"
}

const Wrapper = styled.div<WrapperProps>`
  background: ${({ backgroundType }) => {
    switch (backgroundType) {
      case "gradient":
        return theme.gradient.bg
      case "envelope":
        return theme.color.linearBack
      default:
        return "transparent"
    }
  }};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const ContentWrapper = styled.div`
  display: flex;
  padding: 5em 0;
  justify-content: space-between;
  align-items: flex-start;
  width: 100vw;
  overflow-x: auto;

  @media (${theme.lg}) {
    flex-direction: column;
    width: 100%;
    overflow-x: hidden;
    gap: 48px;
  }
  @media (${theme.sm}) {
    padding: 5em 2em;
  }
`

interface TitleWrapperProps {
  wide: boolean
}

const TitleWrapper = styled.span<TitleWrapperProps>`
  color: ${theme.color.textContrast};
  text-align: ${({ wide }) => (wide ? "center" : "left")};
  width: ${({ wide }) => (wide ? "100%" : "100%")};

  &.primary {
    color: ${theme.color.primary};
  }

  &.disabled {
    color: ${theme.color.textDisabled};
  }

  @media (${theme.lg}) {
    width: 100%;
  }
`

const ItemWrapper = styled.div<{ wide: boolean }>`
  width: ${({ wide }) => (wide ? "100%" : "100%")};
  min-width: 500px;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @media (${theme.lg}) {
    min-width: 0px;
    width: 100%;
  }
`

export const CardWrapper = styled.div<{ wide: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ wide }) => (wide ? "center" : "flex-start")};
  width: 100%;
  max-width: ${({ wide }) => (wide ? "65vw" : "520px")};
  gap: 16px;

  @media (${theme.lg}) {
    // text-align: center;
    max-width: unset;
    width: 100%;
  }
`

const DescWrapper = styled.div<{ wide: boolean; contentBorder: boolean }>`
  width: ${({ wide }) => (wide ? "60%" : "100%")};
  ${props =>
    !props.wide &&
    props.contentBorder &&
    `
  border-left: 3px solid #ccc;
  margin: 1.5em 0;
  padding: 0 10px;
`}
  text-align: ${({ wide }) => (wide ? "center" : "left")};

  color: ${theme.color.textLightGray};
  @media (${theme.lg}) {
    width: 100%;
  }
`

export const SubDescLayoutWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  text-align: center;
  margin-top: 3vh;

  @media (${theme.lg}) {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 48px;
  }

  @media (${theme.sm}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 48px;
  }
`

export const SubDescWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const SubDescTitleWrapper = styled.div`
  color: ${theme.color.primary};
`

export const SubDescContent = styled.div`
  color: ${theme.color.textContrast};
  width: 60%;

  @media (${theme.lg}) {
    display: none;
  }
`

const TopWrapper = styled.div`
  margin-bottom: 3vh;
  width: 100%;
  transform: translateY(-1px);
  @media (${theme.lg}) {
    margin-bottom: 0vh;
  }
`

const BottomWrapper = styled.div`
  margin-top: 3vh;
  width: 100%;
  transform: rotate(180deg) translateY(-1px);
  @media (${theme.lg}) {
    margin-top: 0vh;
  }
`
